<template>
  <transition name="fade">
    <div class="profile-page flex">
      <side-menu />
      <div class="lg:pl-16 flex-grow w-full min-h-screen">
        <NavBar title="Profile" />
        <Loader v-if="loading" />
        <div v-else class="profile-container py-8 px-4 md:px-8">
          <!-- IDENTITY CARD -->
          <section class="profile-card">
            <div class="identity-row">
              <div class="avatar-wrap">
                <img v-if="photoURL" :src="photoURL" alt="avatar" class="avatar" />
                <div v-else class="avatar avatar-fallback">{{ initials }}</div>
              </div>
              <div class="identity-text">
                <div class="display-name">{{ displayName || "Set your display name" }}</div>
                <div class="email">{{ email }}</div>
                <div v-if="memberSince" class="member-since">Member since {{ memberSince }}</div>
              </div>
            </div>
          </section>

          <!-- EDITABLE FIELDS -->
          <section class="profile-card">
            <h3 class="section-title">Account Details</h3>

            <div class="field">
              <label class="field-label">Display Name</label>
              <input
                v-model="form.displayName"
                type="text"
                maxlength="60"
                class="field-input"
                placeholder="How tipsters will see you"
              />
              <p class="field-help">Shown on accumulator share cards and any future leaderboards.</p>
            </div>

            <div class="field">
              <label class="field-label">Email</label>
              <input :value="email" type="email" disabled class="field-input field-input-disabled" />
              <p class="field-help">Email is managed by your sign-in provider and can't be changed here.</p>
            </div>

            <div class="field">
              <label class="field-label">Timezone</label>
              <input :value="timezone" type="text" disabled class="field-input field-input-disabled" />
              <p class="field-help">Auto-detected from your browser.</p>
            </div>

            <div class="actions">
              <button
                class="btn-save"
                :disabled="!hasChanges || saving"
                @click="onSave"
              >
                <span v-if="saving">Saving…</span>
                <span v-else-if="!hasChanges">No changes</span>
                <span v-else>Save changes</span>
              </button>
              <transition name="fade-y" mode="out-in">
                <div v-if="savedAt" class="saved-toast">Profile updated ✓</div>
                <div v-else-if="saveError" class="error-toast">{{ saveError }}</div>
              </transition>
            </div>
          </section>

          <!-- PAYMENT METHODS — placeholder -->
          <section class="profile-card">
            <h3 class="section-title">Payment Methods</h3>
            <div class="payment-empty">
              <div class="payment-icon">💳</div>
              <div class="payment-text">
                <div class="payment-title">Card &amp; PayPal — coming soon</div>
                <div class="payment-sub">
                  We'll let you link a card, PayPal, or Apple/Google Pay here to unlock premium algos, credit packs and Betslip subscriptions. All payments will be processed via Stripe — your card details never touch our servers.
                </div>
              </div>
            </div>
          </section>

          <!-- LINKS -->
          <section class="profile-card profile-links-card">
            <router-link to="/settings" class="link-row">
              <span>Betting preferences</span>
              <span class="link-row-arrow">›</span>
            </router-link>
            <router-link to="/openclaw" class="link-row">
              <span>OpenClaw integration</span>
              <span class="link-row-arrow">›</span>
            </router-link>
          </section>
        </div>
      </div>
      <mob-menu class="lg:hidden" />
    </div>
  </transition>
</template>

<script>
import NavBar from "@/components/organisms/NavBar.vue";
import SideMenu from "@/components/organisms/SideMenu.vue";
import MobMenu from "@/components/organisms/MobMenu.vue";
import Loader from "@/components/atoms/Loader.vue";
import firebase from "@/firebase";
import { format } from "date-fns";
import { mapActions } from "vuex";

export default {
  name: "Profile",
  components: { NavBar, SideMenu, MobMenu, Loader },
  data() {
    return {
      loading: true,
      saving: false,
      savedAt: null,
      saveError: null,
      user: null,
      form: { displayName: "" },
    };
  },
  computed: {
    email() { return this.user?.email || ""; },
    displayName() { return this.user?.displayName || ""; },
    photoURL() { return this.user?.photoURL || ""; },
    initials() {
      const src = this.user?.displayName || this.user?.email || "";
      return src.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase() || "").join("") || "?";
    },
    memberSince() {
      const ts = this.user?.metadata?.creationTime;
      if (!ts) return "";
      try { return format(new Date(ts), "MMMM yyyy"); } catch { return ""; }
    },
    timezone() {
      try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "—"; } catch { return "—"; }
    },
    hasChanges() {
      return (this.form.displayName || "").trim() !== (this.user?.displayName || "");
    },
  },
  async created() {
    this.loading = true;
    try {
      this.user = await firebase.getCurrentUser();
      this.form.displayName = this.user?.displayName || "";
    } catch (e) {
      console.error("profile load failed", e);
    }
    this.loading = false;
  },
  methods: {
    ...mapActions(["updateDevice"]),
    async onSave() {
      if (!this.hasChanges || this.saving) return;
      this.saving = true;
      this.saveError = null;
      this.savedAt = null;
      try {
        const newName = this.form.displayName.trim();
        await this.user.updateProfile({ displayName: newName });
        // Force token refresh so subsequent backend calls reflect the new name claim
        await this.user.getIdToken(true);
        // Existing PUT /api/User re-pulls Firebase user info + upserts user_info + syncs to CRM
        await this.updateDevice({ uuid: localStorage.getItem("_devK") });
        // Re-read current user so the page reflects the change
        this.user = await firebase.getCurrentUser();
        this.savedAt = Date.now();
        setTimeout(() => { this.savedAt = null; }, 4000);
      } catch (e) {
        console.error(e);
        this.saveError = e?.message || "Couldn't save profile";
        setTimeout(() => { this.saveError = null; }, 6000);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.profile-page { min-height: 100vh; }
.profile-container { max-width: 800px; margin: 0 auto; }

.profile-card {
  @apply bg-white dark:bg-bs-dark-bg-over rounded-3xl p-6 mb-6;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.05);
}

.section-title {
  @apply text-lg font-bold text-bs-dark-bg dark:text-white mb-5;
}

/* Identity row */
.identity-row { display: flex; align-items: center; gap: 16px; }
.avatar-wrap { flex-shrink: 0; }
.avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  background: linear-gradient(135deg, #07255B 0%, #1d3a8a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 22px;
  font-weight: 700;
}
.avatar-fallback { letter-spacing: 1px; }
.identity-text { min-width: 0; }
.display-name {
  @apply text-xl font-bold text-bs-dark-bg dark:text-white;
}
.email {
  @apply text-sm text-bs-gray-dark dark:text-white opacity-70;
  word-break: break-all;
}
.member-since {
  @apply text-xs text-bs-gray-dark dark:text-white opacity-60 mt-1;
}

/* Form fields */
.field { margin-bottom: 20px; }
.field-label {
  @apply text-sm font-medium text-bs-gray-dark dark:text-white block mb-2;
}
.field-input {
  @apply w-full py-3 px-4 border border-bs-gray-light rounded-xl bg-white dark:bg-bs-dark-bg dark:text-white dark:border-bs-blue-dark;
  font-size: 14px;
}
.field-input:focus { outline: none; border-color: #407BFF; }
.field-input-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.field-help {
  @apply text-xs text-bs-gray-dark dark:text-white opacity-60 mt-2;
}

/* Actions */
.actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 6px;
}
.btn-save {
  background: #407BFF;
  color: #fff;
  font-weight: 600;
  padding: 11px 22px;
  border-radius: 12px;
  border: 0;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;
}
.btn-save:hover:not(:disabled) { background: #3463cf; }
.btn-save:disabled { opacity: 0.5; cursor: not-allowed; }
.saved-toast {
  @apply text-bs-green text-sm font-medium;
}
.error-toast {
  @apply text-red-600 text-sm font-medium;
}

/* Payment placeholder */
.payment-empty {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  background: linear-gradient(135deg, rgba(64, 123, 255, 0.05), rgba(255, 207, 117, 0.05));
  border: 1px dashed rgba(64, 123, 255, 0.3);
  border-radius: 16px;
  padding: 18px;
}
.payment-icon { font-size: 32px; line-height: 1; }
.payment-text { flex: 1; }
.payment-title {
  @apply font-semibold text-bs-dark-bg dark:text-white mb-1;
  font-size: 15px;
}
.payment-sub {
  @apply text-sm text-bs-gray-dark dark:text-white opacity-70;
  line-height: 1.5;
}

/* Links */
.profile-links-card { padding: 8px 6px; }
.link-row {
  @apply flex items-center justify-between px-5 py-4 text-bs-dark-bg dark:text-white font-medium cursor-pointer rounded-2xl;
  transition: background 0.15s;
}
.link-row:hover { background: rgba(64, 123, 255, 0.08); }
.link-row-arrow { font-size: 22px; opacity: 0.5; }

.fade-y-enter-active, .fade-y-leave-active { transition: opacity 0.3s, transform 0.3s; }
.fade-y-enter-from, .fade-y-leave-to { opacity: 0; transform: translateY(4px); }
</style>
